import React, { Component, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Result } from 'antd';
import { ResultStatusType } from 'antd/lib/result';

import i18n from '../../i18n';

const ERROR_KEYS = {
  403: {
    title: 'errorBoundary.accessDenied',
    subTitle: 'errorBoundary.accessDeniedSubtitle',
  },
  default: {
    title: 'errorBoundary.somethingWentWrong',
    subTitle: 'errorBoundary.somethingWentWrongSubtitle',
  },
};

const getErrorContent = (
  errorCode: ResultStatusType | undefined,
  key: 'title' | 'subTitle',
): string => {
  const keys =
    ERROR_KEYS[errorCode as keyof typeof ERROR_KEYS] ?? ERROR_KEYS.default;
  return i18n.t(keys[key]);
};

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  path?: string;
  errorCode?: ResultStatusType;
}

const withRouter = <P extends object>(
  WrappedComponent: React.ComponentType<
    P & { location: ReturnType<typeof useLocation> }
  >,
) => {
  const WithRouter = (props: P) => {
    const location = useLocation();
    return <WrappedComponent {...props} location={location} />;
  };
  return WithRouter;
};

class ErrorBoundaryBase extends Component<
  ErrorBoundaryProps & { location: ReturnType<typeof useLocation> },
  ErrorBoundaryState
> {
  constructor(
    props: ErrorBoundaryProps & { location: ReturnType<typeof useLocation> },
  ) {
    super(props);
    this.state = {
      hasError: false,
      path: props.location.pathname,
    };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    nextProps: ErrorBoundaryProps & {
      location: ReturnType<typeof useLocation>;
    },
    prevState: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (nextProps.location.pathname !== prevState.path) {
      return {
        hasError: false,
        path: nextProps.location.pathname,
      };
    }
    return null;
  }

  componentDidCatch(error: any, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error', error, errorInfo);
    this.setState({
      errorCode: error?.response?.status,
    });
  }

  render() {
    const { hasError, errorCode } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      return (
        fallback || (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}>
            <Result
              status={errorCode}
              title={getErrorContent(errorCode, 'title')}
              subTitle={getErrorContent(errorCode, 'subTitle')}
            />
          </div>
        )
      );
    }

    return children;
  }
}

const ErrorBoundary = withRouter<ErrorBoundaryProps>(ErrorBoundaryBase);

export default ErrorBoundary;
